import { produce } from 'immer';
import type { ChatCompletionRunner } from 'openai/lib/ChatCompletionRunner';
import type { ChatCompletionStream } from 'openai/lib/ChatCompletionStream';
import { useRef } from 'react';
import { useImmer } from 'use-immer';

import type { ChatProviderMessage } from '../types';
import { ChatContext, type ChatState, type MessageCallback } from './ChatContext';
import { useSystemPrompts } from '#components/chat/hooks/useSystemPrompts';
import type { ChatSessionRef } from '#hooks/useChatSession';

export interface ChatProviderProps {
  session?: ChatSessionRef;
}

export function ChatProvider({ children, session }: React.PropsWithChildren<ChatProviderProps>) {
  const subscribersRef = useRef<Set<MessageCallback>>(new Set());
  const [streamingMessage, setStreamingMessage] = useImmer<ChatCompletionRunner | null>(null);

  const systemPrompts = useSystemPrompts();

  const notifySubscribers: MessageCallback = (message, messages, runner?: ChatCompletionStream) => {
    subscribersRef.current.forEach((callback) => {
      callback(message, messages, runner);
    });
  };

  const onMessage = (callback: MessageCallback) => {
    subscribersRef.current.add(callback);
    console.info('Current subscribers', session?.session?.id, subscribersRef.current);
    // console.debug('Using system prompts:', systemPrompts);
    return () => {
      subscribersRef.current.delete(callback);
    };
  };

  const addMessage: ChatState['addMessage'] = (message) => {
    const { message: inputMessage } = message;
    if (!inputMessage) {
      console.warn('Empty message object in message:', message);
      return;
    }

    const messageObj = produce(message as Required<typeof message>, (draft: ChatProviderMessage) => {
      draft.id = message.id ?? crypto.randomUUID();
      draft.metadata = message.metadata ?? {};
      draft.message = { ...inputMessage };
      draft.message.content = inputMessage?.content;
      draft.finishReason = message.finishReason;
      draft.completionMessage = message.completionMessage ?? inputMessage?.content?.toString();
      draft.timestamp = (message.timestamp ? new Date(message.timestamp) : new Date()).toISOString();
    });

    session?.addMessage(messageObj);

    notifySubscribers(messageObj, session?.session?.messages ?? []);
  };

  return (
    <ChatContext.Provider
      value={{
        session,
        messages: session?.session?.messages ?? [],
        onMessage,
        addMessage,
        setStreamingMessage,
        streamingMessage,
        systemPrompts,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
